import {
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysAi } from '../queryKeys';

import { fetchFilteredProjects } from './fetchFilteredProjects';
import {
  type FilteredProjectsQueryParameters,
  type FilteredProjectsResponse,
} from './types';
import { zodFilteredProjectsQueryParametersSchema } from './validations';

type FilteredProjectsQueryKey = ReturnType<
  typeof queryKeysAi.getFilteredProjects
>;

export const getFilteredProjectsQueryOptions = <
  TSelected = FilteredProjectsResponse,
>({
  queryParameters,
  options,
}: {
  options?: Omit<
    UseQueryOptions<
      FilteredProjectsResponse,
      unknown,
      TSelected,
      FilteredProjectsQueryKey
    >,
    'queryKey' | 'queryFn'
  >;
  queryParameters: FilteredProjectsQueryParameters;
}) => {
  const validatedQueryParameters =
    zodFilteredProjectsQueryParametersSchema.parse(queryParameters);

  return queryOptions<
    FilteredProjectsResponse,
    unknown,
    TSelected,
    FilteredProjectsQueryKey
  >({
    queryFn: async ({ signal }) =>
      fetchFilteredProjects(validatedQueryParameters, { signal }),
    queryKey: queryKeysAi.getFilteredProjects(validatedQueryParameters),
    staleTime: ms('5m'),
    ...options,
  });
};

/**
 * Custom useQuery hook to fetch all AI projects (folders from blob storage) filtered by type.
 * Cached on the server for 30 minutes with background refresh.
 * @param queryParameters - The query parameters to fetch the AI projects.
 * @param options - Query options.
 * @returns The filtered AI Projects.
 * @see https://fa-vestigas-dev.azurewebsites.net/docs#/Metadata/get_filtered_projects_get_projects_get
 */
export const useQueryFilteredProjects = (
  queryParameters: FilteredProjectsQueryParameters,
  options?: Omit<
    UseQueryOptions<FilteredProjectsResponse>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getFilteredProjectsQueryOptions({
      queryParameters,
      options,
    }),
  });
